import React, { useState } from "react";
import { Coffee, Instagram, MapPin, Clock, Leaf, Heart } from "lucide-react";
import { useDemoActions, useDemoSEO, DemoLangSwitch, DemoFooterCTA, StickyActions, buildRestaurantSchema, ReserveButton } from "./demoShared";
import { COMPANY } from "../../config/company";

const NAME = "Norra Café";
const TAG = { de: "Skandinavisches Café & Brunch", en: "Scandinavian Café & Brunch", tr: "İskandinav Kahve & Brunch" };

const ITEMS = [
  { sec: "coffee", img: "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=900&q=80&auto=format&fit=crop", name: { de: "Flat White", en: "Flat White", tr: "Flat White" }, desc: { de: "Single Origin · Mikroschaum-Hafermilch", en: "Single origin · oat micro-foam", tr: "Tek köken · yulaf süt mikro-köpük" }, price: "4,20" },
  { sec: "coffee", img: "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?w=900&q=80&auto=format&fit=crop", name: { de: "Cappuccino", en: "Cappuccino", tr: "Cappuccino" }, desc: { de: "Klassisch italienisch, perfekt aufgeschäumt", en: "Classic Italian, perfectly steamed", tr: "Klasik İtalyan, mükemmel buharlanmış" }, price: "3,80" },
  { sec: "coffee", img: "https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=900&q=80&auto=format&fit=crop", name: { de: "Cold Brew", en: "Cold Brew", tr: "Cold Brew" }, desc: { de: "12 Std. langsam extrahiert, glasklar", en: "12-hour slow extracted, crystal clear", tr: "12 saat yavaş demlenmiş, berrak" }, price: "4,50", tag: "new" },
  { sec: "coffee", img: "https://images.unsplash.com/photo-1536256263959-770b48d82b0a?w=900&q=80&auto=format&fit=crop", name: { de: "Matcha Latte", en: "Matcha Latte", tr: "Matcha Latte" }, desc: { de: "Zeremonien-Matcha, Hafermilch, Honig optional", en: "Ceremonial matcha, oat milk, optional honey", tr: "Tören matcha, yulaf süt, opsiyonel bal" }, price: "5,20", tag: "vegan" },

  { sec: "brunch", img: "https://images.unsplash.com/photo-1541519227354-08fa5d50c44d?w=900&q=80&auto=format&fit=crop", name: { de: "Avocado Sourdough", en: "Avocado Sourdough", tr: "Avokadolu Ekşi Mayalı" }, desc: { de: "Pochiertes Ei, Chili-Flakes, Limette, Microgreens", en: "Poached egg, chili flakes, lime, microgreens", tr: "Çılbır yumurta, pul biber, lime, mikro yeşillik" }, price: "11,90", tag: "popular" },
  { sec: "brunch", img: "https://images.unsplash.com/photo-1528207776546-365bb710ee93?w=900&q=80&auto=format&fit=crop", name: { de: "Buttermilch-Pancakes", en: "Buttermilk Pancakes", tr: "Tereyağlı Pancake" }, desc: { de: "Mit Beerenkompott, Ahornsirup und Mandelblättern", en: "Berry compote, maple syrup, almond flakes", tr: "Yaban meyve kompostosu, akçaağaç şurubu, badem" }, price: "10,50" },
  { sec: "brunch", img: "https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=900&q=80&auto=format&fit=crop", name: { de: "Eggs Benedict", en: "Eggs Benedict", tr: "Yumurta Benedict" }, desc: { de: "English Muffin, Räucherlachs, Sauce Hollandaise", en: "English muffin, smoked salmon, hollandaise", tr: "İngiliz çörek, füme somon, hollandez sos" }, price: "13,90" },
  { sec: "brunch", img: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=900&q=80&auto=format&fit=crop", name: { de: "Granola Bowl", en: "Granola Bowl", tr: "Granola Kase" }, desc: { de: "Hausgemachte Granola, Skyr, Saisonfrucht, Honig", en: "House granola, skyr, seasonal fruit, honey", tr: "Ev yapımı granola, skyr, mevsim meyvesi, bal" }, price: "9,90", tag: "vegan" },

  { sec: "pastry", img: "https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=900&q=80&auto=format&fit=crop", name: { de: "Butter-Croissant", en: "Butter Croissant", tr: "Tereyağlı Kruvasan" }, desc: { de: "AOP-Butter, 72 Std. fermentiert", en: "AOP butter, 72-hour fermented", tr: "AOP tereyağı, 72 saat fermente" }, price: "3,50" },
  { sec: "pastry", img: "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=900&q=80&auto=format&fit=crop", name: { de: "Kanelbulle", en: "Cinnamon Roll", tr: "Tarçınlı Çörek" }, desc: { de: "Schwedischer Klassiker, Kardamom-Note", en: "Swedish classic with cardamom note", tr: "İsveç klasiği, kakuleli" }, price: "3,90", tag: "popular" },
];

const SECTIONS = [
  { id: "coffee", label: { de: "Kaffee", en: "Coffee", tr: "Kahve" }, icon: "☕" },
  { id: "brunch", label: { de: "Brunch", en: "Brunch", tr: "Brunch" }, icon: "🥑" },
  { id: "pastry", label: { de: "Backwerk", en: "Pastry", tr: "Hamur İşi" }, icon: "🥐" },
];

const TAG_META = {
  popular: { de: "BELIEBT", en: "POPULAR", tr: "POPÜLER", color: "bg-zinc-900 text-white" },
  vegan: { de: "VEGAN", en: "VEGAN", tr: "VEGAN", color: "bg-emerald-100 text-emerald-800" },
  new: { de: "NEU", en: "NEW", tr: "YENİ", color: "bg-amber-100 text-amber-800" },
};

export default function DemoCafe() {
  const [lang, setLang] = useState("de");
  const tx = (k) => k[lang] || k.de;
  const { callWaiter, requestBill } = useDemoActions(NAME, lang);

  useDemoSEO({
    lang,
    title: `${NAME} – ${tx(TAG)} | Demo · qrmenu-pro.com`,
    description: tx({
      de: `${NAME} Demo — minimalistisches QR-Menü für ein modernes Café. Flat White, Avocado-Toast, Pancakes, hausgemachte Granola — Bilder & Preise.`,
      en: `${NAME} demo — minimal QR menu for a modern café. Flat white, avocado toast, pancakes, house granola — photos & prices.`,
      tr: `${NAME} demo — modern kafe için minimal QR menü. Flat white, avokadolu tost, pancake, ev granolası — fotoğraf ve fiyatlar.`,
    }),
    schema: buildRestaurantSchema({
      url: "https://qrmenu-pro.com/demo-cafe",
      name: NAME,
      image: ITEMS[4].img,
      priceRange: "€",
      cuisine: "Café · Brunch",
      sameAs: [COMPANY.instagramUrl],
    }),
  });

  return (
    <div className="min-h-screen bg-[#f7f4ee] text-zinc-900 selection:bg-zinc-900 selection:text-white" data-testid="demo-cafe-page">
      {/* HERO */}
      <header className="relative">
        <div className="container mx-auto px-5 sm:px-8 pt-10 sm:pt-14">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 font-bold">Slow · Local · Honest</div>
              <h1 className="mt-3 text-4xl sm:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95]" style={{ fontFamily: "'Georgia', 'Cormorant Garamond', serif" }}>
                Norra<br />
                <span className="italic text-zinc-700">Café</span>
              </h1>
            </div>
            <DemoLangSwitch lang={lang} setLang={setLang} />
          </div>

          <div className="mt-10 grid sm:grid-cols-[1fr_auto] gap-8 items-end pb-10 border-b border-zinc-300/60">
            <p className="text-base sm:text-lg text-zinc-700 max-w-md leading-relaxed">
              {tx({
                de: "Specialty coffee, hausgemachter Brunch und schwedische Backwaren. Alles slow gemacht, alles ehrlich.",
                en: "Specialty coffee, house-made brunch and Swedish pastries. Slow-made, honestly served.",
                tr: "Specialty kahve, ev yapımı brunch ve İsveç hamur işleri. Yavaşça yapılır, dürüstçe sunulur.",
              })}
            </p>
            <div className="flex flex-col gap-2 text-xs text-zinc-500">
              <div className="inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> Marienburger Str. 5, 45665 Recklinghausen</div>
              <div className="inline-flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> Mo–Fr 7:30–18:00 · Sa–So 9:00–17:00</div>
            </div>
          </div>

          {/* CTA row */}
          <div className="mt-6 flex flex-wrap gap-3 pb-12">
            <ReserveButton lang={lang} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm transition-all hover:scale-105" />
            <a href={COMPANY.instagramUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-zinc-300 hover:border-zinc-900 text-zinc-900 font-bold text-sm transition-all" data-testid="demo-instagram">
              <Instagram className="h-4 w-4" />
              @{COMPANY.instagramHandle}
            </a>
          </div>
        </div>
      </header>

      {/* MENU SECTIONS */}
      <main className="container mx-auto px-5 sm:px-8 pb-32 max-w-5xl">
        {SECTIONS.map((s) => {
          const items = ITEMS.filter((i) => i.sec === s.id);
          return (
            <section key={s.id} className="mt-14" data-testid={`demo-section-${s.id}`}>
              <div className="flex items-baseline justify-between gap-4 pb-6 border-b border-zinc-300/60">
                <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: "Georgia, serif" }}>
                  <span className="mr-3">{s.icon}</span>{tx(s.label)}
                </h2>
                <span className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{items.length} items</span>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {items.map((it, i) => (
                  <article key={i} className="group flex items-center gap-4 p-3 rounded-2xl hover:bg-white transition-colors duration-200" data-testid={`demo-item-${s.id}-${i}`}>
                    <img src={it.img} alt={tx(it.name)} className="w-20 h-20 rounded-xl object-cover shrink-0 ring-1 ring-zinc-200" loading="lazy" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <h3 className="font-semibold text-base truncate">{tx(it.name)}</h3>
                        <div className="font-mono text-sm text-zinc-700">{it.price}€</div>
                      </div>
                      <p className="mt-1 text-xs text-zinc-500 leading-relaxed line-clamp-2">{tx(it.desc)}</p>
                      {it.tag && TAG_META[it.tag] && (
                        <span className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] tracking-widest font-bold ${TAG_META[it.tag].color}`}>
                          {TAG_META[it.tag][lang] || TAG_META[it.tag].de}
                        </span>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          );
        })}

        {/* Values */}
        <section className="mt-20 grid sm:grid-cols-3 gap-4">
          {[
            { icon: Coffee, label: { de: "Specialty Bohnen", en: "Specialty Beans", tr: "Specialty Çekirdek" }, desc: { de: "Direct trade aus Äthiopien & Kolumbien", en: "Direct trade from Ethiopia & Colombia", tr: "Etiyopya ve Kolombiya'dan direkt ticaret" } },
            { icon: Leaf, label: { de: "Slow Made", en: "Slow Made", tr: "Yavaş Yapım" }, desc: { de: "Sauerteig fermentiert 72h, alles in-house", en: "Sourdough fermented 72h, all in-house", tr: "Ekşi maya 72 saat, her şey içeride" } },
            { icon: Heart, label: { de: "Lokal & ehrlich", en: "Local & honest", tr: "Yerel & dürüst" }, desc: { de: "Bauern aus dem Münsterland — ohne Umwege", en: "Farmers from Münsterland — directly", tr: "Münsterland çiftçilerinden — direkt" } },
          ].map((v, i) => {
            const Icon = v.icon;
            return (
              <div key={i} className="p-6 rounded-2xl bg-white border border-zinc-200">
                <Icon className="h-5 w-5 text-zinc-700" />
                <h3 className="mt-4 font-semibold text-base">{tx(v.label)}</h3>
                <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{tx(v.desc)}</p>
              </div>
            );
          })}
        </section>

        {/* Instagram */}
        <section className="mt-16 p-8 sm:p-10 rounded-3xl bg-white border border-zinc-200 text-center">
          <Instagram className="h-6 w-6 mx-auto text-zinc-700" />
          <h3 className="mt-3 text-2xl font-light tracking-tight" style={{ fontFamily: "Georgia, serif" }}>
            {tx({ de: "Folge uns für News & Specials", en: "Follow us for news & specials", tr: "Haberler ve özel günler için takip et" })}
          </h3>
          <a href={COMPANY.instagramUrl} target="_blank" rel="noopener noreferrer" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm transition-all hover:scale-105">
            @{COMPANY.instagramHandle} →
          </a>
        </section>
      </main>

      <StickyActions
        lang={lang}
        onWaiter={callWaiter}
        onBill={requestBill}
        theme={{ waiterCls: "bg-zinc-900 text-white hover:bg-zinc-800", billCls: "bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50" }}
      />

      <DemoFooterCTA lang={lang} tone="light" />
    </div>
  );
}
